import { useCallback, useEffect, useState, useContext } from 'react';

import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { ComputedDatum } from '@nivo/pie';

import { axios } from '@vyce/core/src/api';
import { WidgetModuleContext } from '@vyce/core/src/contexts';
import { generateQueryParamsFromObj } from '@vyce/core/src/utils/url';
import { isForbiddenError } from '@vyce/core/src/utils/permissions';
import { useMainDashboardFilter } from '../../../hooks/useMainDashboardFilter';
import { useIsMounted } from '../../../hooks/useIsMounted';

export const useWidgetData = () => {
  const [widgetData, setWidgetData] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const isMounted = useIsMounted();

  const { widget, selectedCompanyId, redirectUrl, mainDashboardFilters, customTeamId, onForbidden } =
    useContext(WidgetModuleContext);
  const history = useHistory();

  const { url: widgetUrl, type: widgetType, system_name: widgetSystemName = '' } = widget;

  const { selectedTeamOptions, getTeamsForRequest } = useMainDashboardFilter({
    mainDashboardFilters,
  });

  const getData = useCallback(
    async (url: string) => {
      if (!selectedCompanyId) {
        return setLoading(false);
      }
      try {
        const requestUrl = `/${url.replace('{company_id}', selectedCompanyId)}`;
        const res = await axios.post(requestUrl, {
          limit: 100,
          team_ids: getTeamsForRequest(
            customTeamId ? customTeamId : selectedTeamOptions.length ? 'selected' : 'all'
          ),
        });
        if (!isMounted.current) return;
        setLoading(false);
        setWidgetData(res.data.data);
      } catch (e) {
        if (!isMounted.current) return;
        setLoading(false);
        // 403 means the widget isn't for this user and the dashboard drops it.
        // Anything else stays as quiet as it was — a failed stat tile has never
        // interrupted the user here.
        if (isForbiddenError(e)) onForbidden?.();
        else console.error(e);
      }
    },
    [selectedCompanyId, customTeamId, getTeamsForRequest, onForbidden]
  );

  const handleChartClick = (datum: ComputedDatum<any>) => {
    if (redirectUrl) {
      const search = history.location.search;
      const currentFilters = queryString.parse(search, { parseNumbers: true });
      const newFilters = datum.data.query_field;
      const query = generateQueryParamsFromObj({ ...currentFilters, ...newFilters, teamId: customTeamId });
      history.push(`${redirectUrl}?${query}`);
    }
  };

  useEffect(() => {
    if (widget.url) {
      getData(widget.url);
    }
  }, [selectedCompanyId, selectedTeamOptions]);

  return {
    data: widgetData,
    loading,
    widgetUrl,
    widgetSystemName,
    selectedCompanyId,
    widgetType,
    handleChartClick,
  };
};
